import { ChangeEvent, forwardRef, useEffect, useState } from "react";
import { useFormContext } from "react-hook-form";
import { useTranslation } from "react-i18next";

import { Button } from "chameleon/lib/components";

import {
  pazeButtonWrapper,
  pazeCardWrapper,
} from "checkout-experience/components/payment-method/pay-options/paze/paze-button.css";
import { PazePaymentMethod } from "checkout-experience/components/payment-method/pay-options/paze/paze-payment-method";
import { useChexContext } from "checkout-experience/providers/chex-context-wrapper";
import {
  CheckoutIntentPayload,
  useJWTPayload,
} from "checkout-query-client/hooks/use-jwt-payload";

import { setGlobalContextProperty } from "datadog-loader/loader";

import { usePaymentOptionContext } from "../../payment-option-provider";
import {
  PazeBillingPreference,
  PazeCheckoutData,
  PazePayloadTypeIndicator,
  PazeShippingPreference,
} from "./types";

type PazeButtonProps = {
  value?: string;
  errorMessage?: string;
  errorMessageKey?: string;
  onChange: (e: ChangeEvent<HTMLInputElement>) => void;
};

export const PazeButton = forwardRef<HTMLDivElement, PazeButtonProps>(
  (props: PazeButtonProps, ref) => {
    const { value, errorMessage, errorMessageKey, onChange } = props;
    const { paymentOption } = usePaymentOptionContext() ?? {};
    const { formId } = paymentOption ?? {};
    const { checkoutSession, checkoutSessionToken = "" } = useChexContext();
    const { register, unregister } = useFormContext();
    const { t } = useTranslation(["combined"]);
    const { data: checkoutIntentPayload } =
      useJWTPayload<CheckoutIntentPayload>(checkoutSessionToken);
    const { data: pazeCheckoutData } = useJWTPayload<PazeCheckoutData>(
      value ?? ""
    );

    const [completeCallResponse, setCompleteCallResponse] =
      useState<string>("");

    const showPazeModal = async () => {
      const { amount, decimalCount } =
        checkoutSession?.orderSummary?.totalAmount ?? {};
      const value = amount
        ? `${(Number(amount) / Math.pow(10, decimalCount ?? 0)).toFixed(2)}`
        : "0.00";
      const qty = checkoutSession?.cartItems?.reduce((total, item) => {
        return total + Number(item.quantity);
      }, 0);
      const transactionValue = {
        transactionCurrencyCode: checkoutSession?.currencyCode ?? "USD",
        transactionAmount: value,
      };

      const hasTransactionValue = value === "0.00" ? {} : { transactionValue };

      /**
       * Assuming that if no country is selected, there will be no shipping address
       * if no shipping address, we do not send it as part of Paze API Request payload
       */
      const finalShippingAddress = !checkoutSession?.shippingAddress?.regionCode
        ? {}
        : {
            finalShippingAddress: {
              name: checkoutSession?.shippingAddress?.recipientName ?? "",
              line1: checkoutSession?.shippingAddress?.line1 ?? "",
              line2: checkoutSession?.shippingAddress?.line2 ?? "",
              line3: "",
              city: checkoutSession?.shippingAddress?.locality ?? "",
              state: checkoutSession?.shippingAddress?.administrativeArea ?? "",
              zip: checkoutSession?.shippingAddress?.postalCode ?? "",
              countryCode: checkoutSession?.shippingAddress?.regionCode ?? "",
            },
          };

      const request = {
        sessionId: checkoutIntentPayload?.cid ?? "",
        intent: value === "0.00" ? "ADD_CARD" : "REVIEW_AND_PAY",
        ...hasTransactionValue,
        enhancedTransactionData: {
          ecomData: {
            cartContainsGiftCard: false,
            orderForPickup: false,
            orderQuantity: `${qty}`,
            orderHighestCost: value,
            ...finalShippingAddress,
          },
        },
      };

      const checkoutRequestPayload = {
        ...request,
        shippingPreference: "NONE" as PazeShippingPreference,
        actionCode: "START_FLOW",
      };
      setGlobalContextProperty({
        name: "pazeCheckoutRequestPayload",
        context: {
          actionCode: checkoutRequestPayload.actionCode,
          sessionId: checkoutRequestPayload.sessionId,
          intent: checkoutRequestPayload.intent,
          transactionValue: checkoutRequestPayload.transactionValue,
        },
      });
      const checkoutResponse = await window.DIGITAL_WALLET_SDK.checkout(
        checkoutRequestPayload
      );
      setGlobalContextProperty({
        name: "pazeCheckoutResponse",
        context: "completed",
      });

      if (checkoutResponse?.result === "COMPLETE") {
        const clonedEvent: ChangeEvent<HTMLInputElement> = {
          target: {
            value: checkoutResponse?.checkoutResponse,
          },
        } as ChangeEvent<HTMLInputElement>;
        onChange(clonedEvent);

        const completeRequestPayload = {
          ...request,
          transactionValue,
          transactionType: "PURCHASE",
          transactionOptions: {
            billingPreference: "NONE" as PazeBillingPreference,
            payloadTypeIndicator: "PAYMENT" as PazePayloadTypeIndicator,
          },
        };
        setGlobalContextProperty({
          name: "pazeCompleteRequestPayload",
          context: {
            sessionId: completeRequestPayload.sessionId,
            intent: completeRequestPayload.intent,
            transactionType: completeRequestPayload.transactionType,
            transactionValue: completeRequestPayload.transactionValue,
            transactionOptions: completeRequestPayload.transactionOptions,
          },
        });
        const completeResponse = await window.DIGITAL_WALLET_SDK.complete(
          completeRequestPayload
        );
        setCompleteCallResponse(completeResponse.completeResponse);
        setGlobalContextProperty({
          name: "pazeCompleteResponse",
          context: "completed",
        });
      }
    };

    useEffect(() => {
      if (completeCallResponse && pazeCheckoutData) {
        register(`${formId}.pazeCompleteToken`, {
          value: completeCallResponse,
        });
        register(`${formId}.pazeLastFour`, {
          value: pazeCheckoutData.maskedCard.panLastFour,
        });
        register(`${formId}.network`, {
          value: pazeCheckoutData.maskedCard.paymentCardNetwork,
        });
        register(`${formId}.billingAddressCountry`, {
          value: pazeCheckoutData?.maskedCard?.billingAddress.countryCode,
        });
        register(`${formId}.billingAddressAddressLine1`, {
          value: pazeCheckoutData?.maskedCard?.billingAddress.line1,
        });
        register(`${formId}.billingAddressAddressLine2`, {
          value: pazeCheckoutData?.maskedCard?.billingAddress.line2,
        });
        register(`${formId}.billingAddressCity`, {
          value: pazeCheckoutData?.maskedCard?.billingAddress.city,
        });
        register(`${formId}.billingAddressAdministrativeArea`, {
          value: pazeCheckoutData?.maskedCard?.billingAddress.state,
        });
        register(`${formId}.billingAddressZip`, {
          value: pazeCheckoutData?.maskedCard?.billingAddress.zip,
        });
      }
    }, [completeCallResponse, pazeCheckoutData]);

    const removePazeLink = () => {
      const clonedEvent: ChangeEvent<HTMLInputElement> = {
        target: {
          value: "",
        },
      } as ChangeEvent<HTMLInputElement>;
      onChange(clonedEvent);

      unregister(`${formId}.pazeCompleteToken`);
      unregister(`${formId}.billingAddressCountry`);
      unregister(`${formId}.billingAddressAddressLine1`);
      unregister(`${formId}.billingAddressAddressLine2`);
      unregister(`${formId}.billingAddressCity`);
      unregister(`${formId}.billingAddressAdministrativeArea`);
      unregister(`${formId}.billingAddressZip`);
      setCompleteCallResponse("");
    };

    return (
      <div ref={ref}>
        {pazeCheckoutData?.maskedCard?.panLastFour ? (
          <div className={pazeCardWrapper}>
            <PazePaymentMethod
              last4={pazeCheckoutData?.maskedCard?.panLastFour}
              network={pazeCheckoutData?.maskedCard?.paymentCardNetwork}
            />
            <Button
              id="remove-paze-btn"
              testId="remove-paze-btn"
              text={t("paze.remove")}
              variant="text"
              onClick={removePazeLink}
            />
          </div>
        ) : (
          <div className={pazeButtonWrapper}>
            <Button
              id="link-with-paze-btn"
              testId="link-with-paze-btn"
              text={t("checkoutForm.linkWithPaze.title")}
              variant="outline"
              errorMessage={errorMessage}
              errorMessageKey={errorMessageKey}
              tabIndex={0}
              onClick={showPazeModal}
            />
          </div>
        )}
      </div>
    );
  }
);

PazeButton.displayName = "PazeButton";
